import { Box, Code, Heading, Text, VStack } from "@chakra-ui/react";
import { Element } from "react-scroll";
import CodeBlock from "./CodeBlock";
import DocsTable from "./DocsTable";

export default function ReverseGeocodeDocs() {
  return (
    <VStack align="stretch" spacing={10} w="full">

      <Element name="baseurl">
        <Box>
        <Heading as="h2">Base URL</Heading>
        <Text as="p" fontSize="lg" mt={5}>
          <Code>https://api.potterstech.com/v1/</Code>
        </Text>
        </Box>
      </Element>

      <Element name="endpoint">
        <Box>
        <Heading as="h2">Endpoint</Heading>
        <Text as="p" fontSize="lg" mt={5}>
          <Code>GET /geocode/reverse</Code>
        </Text>
        <Text as="p" fontSize="lg" mt={5}>
          <Text as="span" fontWeight="bold" textDecoration="none">
            Full URL:
          </Text>{" "}
          <Code>https://api.potterstech.com/v1/geocode/reverse</Code>
        </Text>
        </Box>
      </Element>

      <Element name="authentication">
        <Box>
        <Heading as="h2">Authentication</Heading>
        <Text as="p" fontSize="lg" mt={5}>
          <Code>X-Api-Key: &lt;YOUR_API_KEY&gt;</Code>
        </Text>
        </Box>
      </Element>

      <Element name="headers">
        <Box>
        <Heading as="h2">Headers</Heading>
      <DocsTable
        headers={["Header", "Required", "Description"]}
        rows={[
          ["Content-Type", "yes", "application/json"],
          ["X-Api-Key", "yes", "Your assigned API key"],
        ]}
      />
        </Box>
      </Element>

      <Element name="requestparameters">
        <Box>
        <Heading as="h2" mb={5}>Request Parameters</Heading>
        <Text as="b" mt={5}>
          Required
        </Text>
        <DocsTable
          headers={["Parameter", "Type", "Description"]}
          rows={[["latlng", "string", "Latitude and longitude in lat,lng format"]]}
        />
        <Text as="p" fontSize="lg" mt={5}>
          <Text as="span" fontWeight="bold" textDecoration="none">
            Example:
          </Text>{" "}
          <Code>17.413066,78.469743</Code>
        </Text>
        </Box>
      </Element>

      <Element name="requestexamples">
        <Box>
        <Heading as="h2">Request Example</Heading>
        <CodeBlock containerProps={{ mt: 8 }}>
          {`GET /geocode/reverse?latlng=17.413066,78.469743`}
        </CodeBlock>
        </Box>
      </Element>

      <Element name="responsebody">
        <Box>
        <Heading as="h2">Response Body</Heading>
        <CodeBlock containerProps={{ mt: 8 }}>
        {`{
  "results": [
    {
      "place_id": "06344294434493581924",
      "address_components": [
        {
          "long_name": "India",
          "short_name": "",
          "types": ["COUNTRY"]
        }
        // ...
      ],
      "formatted_address": "NTR Gardens., Khairatabad, Hydrabad, Telangana, India",
      "geometry": {
        "location": { "lat": 17.413087, "lng": 78.469752 },
        "location_type": null,
        "viewport": null
      },
      "plus_code": {
        "compound_code": "",
        "global_code": ""
      },
      "types": ["POI_NAME"]
    }
    // ...
  ]
}`}
      </CodeBlock>
        </Box>
      </Element>

      <Element name="responsefields">
        <Box>
        <Heading as="h2" mb={5}>Response Field Reference</Heading>
        <Text as="b" mt={5}>
          results[]
        </Text>
        <DocsTable
          headers={["Field", "Type", "Description"]}
          rows={[
            ["place_id", "string", "Unique identifier for the place"],
            ["formatted_address", "string", "Human-readable address"],
            ["types", "array", "Classification of the place"],
            ["address_components", "array", "Structured breakdown of the address"],
            ["geometry", "object", "Geographic information"],
            ["plus_code", "object", "Plus code details (if available)"],
          ]}
        />

        <Text as="b" mt={8}>
          address_components[]
        </Text>
        <DocsTable
          headers={["Field", "Type", "Description"]}
          rows={[
            ["long_name", "string", "Full name of the component"],
            ["short_name", "string", "Abbreviated name (may be empty)"],
            ["types", "array", "Component type (COUNTRY, STATE, CITY, etc.)"],
          ]}
        />

        <Text as="b" mt={8}>
          geometry
        </Text>
        <DocsTable
          headers={["Field", "Type", "Description"]}
          rows={[
            ["location.lat", "number", "Latitude"],
            ["location.lng", "number", "Longitude"],
            ["location_type", "string/null", "Reserved"],
            ["viewport", "object/null", "Reserved"],
          ]}
        />
        </Box>
      </Element>

      <Element name="examplerequests">
        <Box>
        <Heading as="h2" mb={5}>Example Requests</Heading>
        <Text as="b" mt={5}>
          cURL
        </Text>
        <CodeBlock containerProps={{ mt: 8 }} variant="dark">
        {`curl -X GET \\
  'https://api.potterstech.com/v1/geocode/reverse?latlng=17.413066,78.469743' \\
  -H 'Content-Type: application/json' \\
  -H 'X-Api-Key: YOUR_API_KEY'`}
      </CodeBlock>
        <Text as="b" mt={8}>
          JavaScript
        </Text>
        <CodeBlock containerProps={{ mt: 8 }} variant="dark">
        {`const res = await fetch(
  "https://api.potterstech.com/v1/geocode/reverse?latlng=17.413066,78.469743",
  {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "X-Api-Key": process.env.POTTERS_API_KEY
    }
  }
);

console.log(await res.json());`}
      </CodeBlock>
        <Text as="b" mt={8}>
          Python
        </Text>
        <CodeBlock containerProps={{ mt: 8 }} variant="dark">
        {`import os, requests

url = "https://api.potterstech.com/v1/geocode/reverse"
params = { "latlng": "17.413066,78.469743" }
headers = {
  "Content-Type": "application/json",
  "X-Api-Key": os.environ["POTTERS_API_KEY"]
}

resp = requests.get(url, headers=headers, params=params)
print(resp.json())`}
      </CodeBlock>
        </Box>
      </Element>

      <Element name="statuscodes">
        <Box>
        <Heading as="h2">Status Codes</Heading>
        <DocsTable
          headers={["Code", "Description"]}
          rows={[
            ["200", "Reverse geocoding successful"],
            ["400", "Invalid or missing latlng"],
            ["401", "Invalid or missing API key"],
            ["403", "Access forbidden"],
            ["429", "Rate limit exceeded"],
            ["5xx", "Server error"],
          ]}
        />
        </Box>
      </Element>

      <Element name="notes">
        <Box>
        <Heading as="h2">Notes</Heading>
        <Text as="p" fontSize="lg" mt={5}>
          <ul>
            <li>Results are ordered by proximity and relevance to the input
            coordinates.</li>
            <li>Multiple nearby POIs or administrative areas may be returned.</li>
            <li>This API is suitable for converting map clicks or GPS readings
            into addresses.</li>
            <li><Code>formatted_address</Code> is intended for display; use{" "}
            <Code>address_components</Code> for structured logic.</li>
          </ul>
        </Text>
        </Box>
      </Element>
    </VStack>
  );
}

